// src/pages/Pricing.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import UserAccessSummary from "../components/layout/UserAccessSummary";
import useAccessOverrideVersion from "../hooks/useAccessOverrideVersion";
import { getCurrentUser } from "../utils/auth";
import { getUserAccessInfo } from "../utils/accessFilters";
import { buildAccessHeaders } from "../utils/accessHeaders";
import { usePermissions } from "../utils/permissions";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.portpilot.co";
const PRICING_RULES_ALL_ENDPOINT = "/api/pricing-rules/all";

const emptyRuleForm = {
  price: "",
  effective_from: "",
  effective_to: "",
};

const normalizeCustCode = (value) => String(value ?? "").trim().toUpperCase();

const getProductId = (row) => {
  if (!row) return null;
  if (row.id !== null && row.id !== undefined) return row.id;
  if (row.product_id !== null && row.product_id !== undefined) return row.product_id;
  if (row.productId !== null && row.productId !== undefined) return row.productId;
  return null;
};

const formatProductLabel = (product) => {
  if (!product) return "";
  const cust = product.cust_code ?? product.custCode ?? "";
  const sku = product.retail_sku ?? product.retailSku ?? "";
  const code = product.product_code ?? product.productCode ?? "";
  const name = product.item_name ?? product.itemName ?? "";
  const parts = [cust, sku, code, name].map((value) => String(value || "").trim()).filter(Boolean);
  return parts.join(" | ");
};

const normalizeDateValue = (value) => {
  if (!value) return "";
  return String(value).slice(0, 10);
};

const formatRulePrice = (value) => {
  if (value === null || value === undefined || value === "") return "N/A";
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  return num.toFixed(2);
};

const formatEffectiveTo = (value) => normalizeDateValue(value) || "No expiration";

function AccessDenied() {
  return (
    <div className="flex h-full flex-col items-center justify-center px-4 text-center">
      <h1 className="text-2xl font-semibold text-gray-800">Access denied.</h1>
      <p className="mt-2 max-w-md text-sm text-gray-600">
        Your account does not have access to this page. Please contact your administrator.
      </p>
    </div>
  );
}

export default function Pricing() {
  const me = getCurrentUser();
  const meEmail = me?.email || "";
  const meRole = me?.role || "";
  const accessVersion = useAccessOverrideVersion();
  const accessInfo = useMemo(() => {
    void accessVersion;
    return getUserAccessInfo();
  }, [accessVersion]);
  const requestHeaders = useMemo(
    () => buildAccessHeaders({ email: meEmail, role: meRole }, accessInfo, { includeCust: true, includeWhs: false }),
    [meEmail, meRole, accessInfo]
  );
  const { getLevel } = usePermissions();
  const permissionLevel = getLevel(me?.role, "pricing");
  const canEdit = permissionLevel === "Full";

  const [viewMode, setViewMode] = useState("list");
  const [listFilters, setListFilters] = useState({
    custCode: "",
    query: "",
    dateFrom: "",
    dateTo: "",
  });
  const [allRules, setAllRules] = useState([]);
  const [allRulesLoading, setAllRulesLoading] = useState(false);
  const [allRulesError, setAllRulesError] = useState("");
  const allRulesRequestIdRef = useRef(0);

  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsError, setProductsError] = useState("");
  const [productQuery, setProductQuery] = useState("");
  const [productCustFilter, setProductCustFilter] = useState("");
  const [selectedProductId, setSelectedProductId] = useState("");
  const [showProductOptions, setShowProductOptions] = useState(false);
  const productInputRef = useRef(null);

  const [rules, setRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [rulesError, setRulesError] = useState("");
  const [info, setInfo] = useState("");
  const [pendingEditRule, setPendingEditRule] = useState(null);

  const [newRule, setNewRule] = useState({ ...emptyRuleForm });
  const [editingId, setEditingId] = useState(null);
  const [editRule, setEditRule] = useState({ ...emptyRuleForm });

  useEffect(() => {
    let cancelled = false;
    const loadProducts = async () => {
      setProductsLoading(true);
      setProductsError("");
      try {
        const resp = await fetch(`${API_BASE}/mrp/products`, { headers: requestHeaders });
        const payload = await resp.json().catch(() => ({}));
        if (!resp.ok || payload?.ok === false) {
          throw new Error(payload?.error || "Unable to load products.");
        }
        if (cancelled) return;
        const rows = Array.isArray(payload?.rows) ? payload.rows : [];
        const normalized = rows.map((row) => ({
          ...row,
          id: getProductId(row),
          cust_code: normalizeCustCode(row.cust_code ?? row.custCode),
        }));
        setProducts(normalized);
      } catch (err) {
        if (!cancelled) setProductsError(err?.message || "Unable to load products.");
      } finally {
        if (!cancelled) setProductsLoading(false);
      }
    };
    loadProducts();
    return () => {
      cancelled = true;
    };
  }, [requestHeaders]);

  const custOptions = useMemo(() => {
    const rows = Array.isArray(products) ? products : [];
    const seen = new Set();
    rows.forEach((row) => {
      const cust = normalizeCustCode(row.cust_code ?? row.custCode);
      if (cust) seen.add(cust);
    });
    return Array.from(seen).sort();
  }, [products]);

  const listCustOptions = useMemo(() => {
    const source = products.length ? products : allRules;
    const seen = new Set();
    source.forEach((row) => {
      const cust = normalizeCustCode(row.cust_code ?? row.custCode);
      if (cust) seen.add(cust);
    });
    return Array.from(seen).sort();
  }, [products, allRules]);

  const productOptions = useMemo(() => {
    const term = productQuery.trim().toLowerCase();
    const rows = Array.isArray(products) ? products : [];
    const custValue = normalizeCustCode(productCustFilter);
    const filtered = rows.filter((row) => {
      if (custValue) {
        const rowCust = normalizeCustCode(row.cust_code ?? row.custCode);
        if (rowCust !== custValue) return false;
      }
      if (term) {
        return formatProductLabel(row).toLowerCase().includes(term);
      }
      return true;
    });
    return filtered
      .map((row) => ({
        id: getProductId(row),
        label: formatProductLabel(row),
      }))
      .filter((entry) => entry.id !== undefined && entry.id !== null)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [products, productQuery, productCustFilter]);

  const loadAllRules = useCallback(
    async (filters) => {
      const requestId = allRulesRequestIdRef.current + 1;
      allRulesRequestIdRef.current = requestId;
      setAllRulesLoading(true);
      setAllRulesError("");
      try {
        const params = new URLSearchParams();
        if (filters?.custCode) params.set("cust_code", filters.custCode);
        if (filters?.query) params.set("q", filters.query.trim());
        if (filters?.dateFrom) params.set("date_from", filters.dateFrom);
        if (filters?.dateTo) params.set("date_to", filters.dateTo);
        params.set("limit", "500");
        const resp = await fetch(`${API_BASE}${PRICING_RULES_ALL_ENDPOINT}?${params.toString()}`, {
          headers: requestHeaders,
        });
        const payload = await resp.json().catch(() => ({}));
        if (!resp.ok || payload?.ok === false) {
          throw new Error(payload?.message || payload?.error || "Unable to load pricing rules.");
        }
        if (requestId !== allRulesRequestIdRef.current) return;
        const rows = Array.isArray(payload?.rows) ? payload.rows : [];
        const normalized = rows.map((row) => ({
          pricing_rule_id: row.pricing_rule_id ?? row.id,
          product_id: row.product_id ?? row.productId,
          cust_code: normalizeCustCode(row.cust_code ?? row.custCode),
          retail_sku: row.retail_sku ?? row.retailSku,
          product_code: row.product_code ?? row.productCode,
          item_name: row.item_name ?? row.itemName,
          price: row.price,
          effective_from: row.effective_from,
          effective_to: row.effective_to,
          updated_at: row.updated_at,
        }));
        setAllRules(normalized);
      } catch (err) {
        if (requestId !== allRulesRequestIdRef.current) return;
        setAllRulesError(err?.message || "Unable to load pricing rules.");
      } finally {
        if (requestId === allRulesRequestIdRef.current) setAllRulesLoading(false);
      }
    },
    [requestHeaders]
  );

  useEffect(() => {
    if (permissionLevel === "None") return;
    const timer = window.setTimeout(() => {
      loadAllRules(listFilters);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [listFilters, loadAllRules, permissionLevel]);

  useEffect(() => {
    if (!selectedProductId || productsLoading) return;
    const hasMatch = productOptions.some((option) => String(option.id) === String(selectedProductId));
    if (!hasMatch) setSelectedProductId("");
  }, [productOptions, selectedProductId, productsLoading]);

  useEffect(() => {
    if (!listFilters.custCode) return;
    if (!listCustOptions.includes(listFilters.custCode)) {
      setListFilters((prev) => ({ ...prev, custCode: "" }));
    }
  }, [listCustOptions, listFilters.custCode]);

  useEffect(() => {
    if (!productCustFilter) return;
    if (!custOptions.includes(productCustFilter)) {
      setProductCustFilter("");
    }
  }, [custOptions, productCustFilter]);

  useEffect(() => {
    if (!selectedProductId) return;
    const current = productOptions.find((option) => String(option.id) === String(selectedProductId));
    if (current && current.label !== productQuery) {
      setProductQuery(current.label);
    }
  }, [selectedProductId, productOptions, productQuery]);

  useEffect(() => {
    if (viewMode !== "detail" || !selectedProductId) {
      setRules([]);
      return;
    }
    let cancelled = false;
    const loadRules = async () => {
      setRulesLoading(true);
      setRulesError("");
      try {
        const resp = await fetch(`${API_BASE}/api/pricing-rules?product_id=${selectedProductId}`, {
          headers: requestHeaders,
        });
        const payload = await resp.json().catch(() => ({}));
        if (!resp.ok || payload?.ok === false) {
          throw new Error(payload?.message || payload?.error || "Unable to load pricing rules.");
        }
        if (cancelled) return;
        setRules(Array.isArray(payload?.rules) ? payload.rules : []);
      } catch (err) {
        if (!cancelled) setRulesError(err?.message || "Unable to load pricing rules.");
      } finally {
        if (!cancelled) setRulesLoading(false);
      }
    };
    loadRules();
    return () => {
      cancelled = true;
    };
  }, [selectedProductId, requestHeaders, viewMode]);

  useEffect(() => {
    if (!pendingEditRule || !selectedProductId) return;
    if (String(selectedProductId) !== String(pendingEditRule.product_id || pendingEditRule.productId || "")) {
      return;
    }
    const ruleId = pendingEditRule.pricing_rule_id ?? pendingEditRule.id;
    if (!ruleId) return;
    setEditingId(ruleId);
    setEditRule({
      price: pendingEditRule.price ?? "",
      effective_from: normalizeDateValue(pendingEditRule.effective_from),
      effective_to: normalizeDateValue(pendingEditRule.effective_to),
    });
    setPendingEditRule(null);
  }, [pendingEditRule, selectedProductId]);

  const resetMessages = () => {
    setInfo("");
    setRulesError("");
    setAllRulesError("");
  };

  const handleCreateRule = async () => {
    if (!canEdit) return;
    resetMessages();
    if (!selectedProductId) {
      setRulesError("Please select a product first.");
      return;
    }
    if (!newRule.price || !newRule.effective_from) {
      setRulesError("Price and Effective From are required.");
      return;
    }
    try {
      const headers = {
        "Content-Type": "application/json",
        ...requestHeaders,
      };
      const resp = await fetch(`${API_BASE}/api/pricing-rules`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          product_id: Number(selectedProductId),
          price: newRule.price,
          effective_from: newRule.effective_from,
          effective_to: newRule.effective_to || null,
        }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok || payload?.ok === false) {
        throw new Error(payload?.message || payload?.error || "Unable to create pricing rule.");
      }
      setNewRule({ ...emptyRuleForm });
      setInfo("Pricing rule created.");
      setRules(Array.isArray(payload?.rule) ? payload.rule : payload?.rule ? [payload.rule, ...rules] : rules);
      // Refresh full list for correct ordering
      setSelectedProductId((value) => String(value));
      loadAllRules(listFilters);
    } catch (err) {
      setRulesError(err?.message || "Unable to create pricing rule.");
    }
  };

  const startEditRule = (rule) => {
    if (!canEdit) return;
    setEditingId(rule.id);
    setEditRule({
      price: rule.price ?? "",
      effective_from: normalizeDateValue(rule.effective_from),
      effective_to: normalizeDateValue(rule.effective_to),
    });
  };

  const cancelEditRule = () => {
    setEditingId(null);
    setEditRule({ ...emptyRuleForm });
  };

  const handleUpdateRule = async () => {
    if (!canEdit || !editingId) return;
    resetMessages();
    if (!editRule.price || !editRule.effective_from) {
      setRulesError("Price and Effective From are required.");
      return;
    }
    try {
      const headers = {
        "Content-Type": "application/json",
        ...requestHeaders,
      };
      const resp = await fetch(`${API_BASE}/api/pricing-rules/${editingId}`, {
        method: "PUT",
        headers,
        body: JSON.stringify({
          price: editRule.price,
          effective_from: editRule.effective_from,
          effective_to: editRule.effective_to || null,
        }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok || payload?.ok === false) {
        throw new Error(payload?.message || payload?.error || "Unable to update pricing rule.");
      }
      setInfo("Pricing rule updated.");
      setEditingId(null);
      setEditRule({ ...emptyRuleForm });
      setRules((prev) =>
        prev.map((rule) => (rule.id === payload?.rule?.id ? payload.rule : rule))
      );
      loadAllRules(listFilters);
    } catch (err) {
      setRulesError(err?.message || "Unable to update pricing rule.");
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!canEdit) return;
    resetMessages();
    if (!window.confirm("Delete this pricing rule?")) return;
    try {
      const resp = await fetch(`${API_BASE}/api/pricing-rules/${ruleId}`, {
        method: "DELETE",
        headers: requestHeaders,
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok || payload?.ok === false) {
        throw new Error(payload?.message || payload?.error || "Unable to delete pricing rule.");
      }
      setRules((prev) => prev.filter((rule) => rule.id !== ruleId));
      setInfo("Pricing rule deleted.");
      loadAllRules(listFilters);
    } catch (err) {
      setRulesError(err?.message || "Unable to delete pricing rule.");
    }
  };

  const openAddView = () => {
    resetMessages();
    setViewMode("detail");
    setEditingId(null);
    setEditRule({ ...emptyRuleForm });
    setNewRule({ ...emptyRuleForm });
    setPendingEditRule(null);
    setSelectedProductId("");
    setProductQuery("");
    setProductCustFilter("");
    setShowProductOptions(false);
  };

  const handleEditFromList = (row) => {
    if (!canEdit) return;
    resetMessages();
    setViewMode("detail");
    const productId = row?.product_id ?? row?.productId;
    if (productId !== undefined && productId !== null) {
      setSelectedProductId(String(productId));
    }
    const cust = normalizeCustCode(row?.cust_code ?? row?.custCode);
    if (cust) setProductCustFilter(cust);
    setProductQuery(formatProductLabel(row));
    setShowProductOptions(false);
    setPendingEditRule({
      ...row,
      pricing_rule_id: row?.pricing_rule_id ?? row?.id,
    });
  };

  const handleBackToList = () => {
    resetMessages();
    setViewMode("list");
    setEditingId(null);
    setEditRule({ ...emptyRuleForm });
    setPendingEditRule(null);
    loadAllRules(listFilters);
  };

  if (permissionLevel === "None") {
    return <AccessDenied />;
  }

  return (
    <div className="p-4 space-y-4">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold text-indigo-700">Pricing</h1>
          <p className="text-sm text-slate-600">
            Review pricing rules across products, then drill down to edit or add new rules.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <UserAccessSummary />
          {viewMode === "list" && (
            <button
              type="button"
              onClick={openAddView}
              disabled={!canEdit}
              className="inline-flex items-center justify-center rounded bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Add Pricing Rule
            </button>
          )}
        </div>
      </header>

      {viewMode === "list" ? (
        <>
          {allRulesError && (
            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {allRulesError}
            </div>
          )}
          {info && (
            <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
              {info}
            </div>
          )}
          {permissionLevel !== "Full" && (
            <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
              You have read-only access to Pricing rules.
            </div>
          )}

          <section className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-[180px]">
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Cust Code
                </label>
                <select
                  value={listFilters.custCode}
                  onChange={(event) => setListFilters((prev) => ({ ...prev, custCode: event.target.value }))}
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">All Cust Codes</option>
                  {listCustOptions.map((cust) => (
                    <option key={cust} value={cust}>
                      {cust}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1 min-w-[220px]">
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Search
                </label>
                <input
                  type="text"
                  value={listFilters.query}
                  onChange={(event) => setListFilters((prev) => ({ ...prev, query: event.target.value }))}
                  placeholder="Search SKU, product code, or name"
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div className="min-w-[160px]">
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Date From
                </label>
                <input
                  type="date"
                  value={listFilters.dateFrom}
                  onChange={(event) => setListFilters((prev) => ({ ...prev, dateFrom: event.target.value }))}
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div className="min-w-[160px]">
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Date To
                </label>
                <input
                  type="date"
                  value={listFilters.dateTo}
                  onChange={(event) => setListFilters((prev) => ({ ...prev, dateTo: event.target.value }))}
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={() =>
                    setListFilters({
                      custCode: "",
                      query: "",
                      dateFrom: "",
                      dateTo: "",
                    })
                  }
                  className="rounded border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                >
                  Clear
                </button>
              </div>
            </div>
            {allRulesLoading && <div className="text-xs text-slate-500">Loading pricing rules...</div>}
          </section>

          <section className="rounded-lg border border-slate-200 bg-white p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-800">All Pricing Rules</h2>
              <div className="text-xs text-slate-500">
                {allRulesLoading ? "Loading rules..." : `${allRules.length} rules`}
              </div>
            </div>
            <div className="overflow-auto border rounded">
              <table className="min-w-full border-collapse text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="border px-3 py-2 text-left">Cust Code</th>
                    <th className="border px-3 py-2 text-left">Retail SKU</th>
                    <th className="border px-3 py-2 text-left">Product Code</th>
                    <th className="border px-3 py-2 text-left">Item Name</th>
                    <th className="border px-3 py-2 text-left">Price</th>
                    <th className="border px-3 py-2 text-left">Effective From</th>
                    <th className="border px-3 py-2 text-left">Effective To</th>
                    <th className="border px-3 py-2 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allRules.length === 0 && !allRulesLoading && (
                    <tr>
                      <td className="px-3 py-4 text-center text-slate-500" colSpan={8}>
                        No pricing rules found.
                      </td>
                    </tr>
                  )}
                  {allRules.map((row) => (
                    <tr key={row.pricing_rule_id} className="border-t">
                      <td className="px-3 py-2">{row.cust_code || "N/A"}</td>
                      <td className="px-3 py-2">{row.retail_sku || "N/A"}</td>
                      <td className="px-3 py-2">{row.product_code || "N/A"}</td>
                      <td className="px-3 py-2">{row.item_name || "N/A"}</td>
                      <td className="px-3 py-2">{formatRulePrice(row.price)}</td>
                      <td className="px-3 py-2">{normalizeDateValue(row.effective_from) || "N/A"}</td>
                      <td className="px-3 py-2">{formatEffectiveTo(row.effective_to)}</td>
                      <td className="px-3 py-2">
                        {canEdit ? (
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              className="text-xs text-blue-600 hover:underline"
                              onClick={() => handleEditFromList(row)}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="text-xs text-red-600 hover:underline"
                              onClick={() => handleDeleteRule(row.pricing_rule_id)}
                            >
                              Delete
                            </button>
                          </div>
                        ) : (
                          "N/A"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <button
              type="button"
              onClick={handleBackToList}
              className="inline-flex items-center gap-2 rounded border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Back to All Pricing Rules
            </button>
          </div>

          {productsError && (
            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {productsError}
            </div>
          )}
          {rulesError && (
            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {rulesError}
            </div>
          )}
          {info && (
            <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
              {info}
            </div>
          )}

          {permissionLevel !== "Full" && (
            <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
              You have read-only access to Pricing rules.
            </div>
          )}

          <section className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Cust Code
                </label>
                <select
                  value={productCustFilter}
                  onChange={(event) => setProductCustFilter(event.target.value)}
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">All Cust Codes</option>
                  {custOptions.map((cust) => (
                    <option key={cust} value={cust}>
                      {cust}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1 min-w-[260px]">
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Select product
                </label>
                <div className="relative mt-1">
                  <input
                    ref={productInputRef}
                    type="text"
                    value={productQuery}
                    onChange={(event) => {
                      setProductQuery(event.target.value);
                      setShowProductOptions(true);
                    }}
                    onFocus={() => setShowProductOptions(true)}
                    onBlur={() => {
                      window.setTimeout(() => setShowProductOptions(false), 120);
                    }}
                    placeholder="Type to filter products"
                    className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  {showProductOptions && (
                    <div className="absolute z-10 mt-1 max-h-64 w-full overflow-auto rounded border border-slate-200 bg-white shadow-lg">
                      {productOptions.length ? (
                        productOptions.map((option) => (
                          <button
                            key={option.id}
                            type="button"
                            onMouseDown={(event) => {
                              event.preventDefault();
                              setSelectedProductId(String(option.id));
                              setProductQuery(option.label);
                              setShowProductOptions(false);
                            }}
                            className={`flex w-full items-start px-3 py-2 text-left text-sm hover:bg-slate-100 ${
                              String(option.id) === String(selectedProductId) ? "bg-slate-100" : ""
                            }`}
                          >
                            {option.label}
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-sm text-slate-500">
                          No products match the current filters.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            {productsLoading && <div className="text-xs text-slate-500">Loading products...</div>}
          </section>

          <section className="rounded-lg border border-slate-200 bg-white p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-800">Pricing rules</h2>
              <div className="text-xs text-slate-500">
                {rulesLoading ? "Loading rules..." : `${rules.length} rules`}
              </div>
            </div>

            <div className="overflow-auto border rounded">
              <table className="min-w-full border-collapse text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="border px-3 py-2 text-left">Price</th>
                    <th className="border px-3 py-2 text-left">Effective From</th>
                    <th className="border px-3 py-2 text-left">Effective To</th>
                    <th className="border px-3 py-2 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rules.length === 0 && (
                    <tr>
                      <td className="px-3 py-4 text-center text-slate-500" colSpan={4}>
                        {selectedProductId ? "No pricing rules found." : "Select a product to view rules."}
                      </td>
                    </tr>
                  )}
                  {rules.map((rule) => {
                    const isEditing = editingId === rule.id;
                    return (
                      <tr key={rule.id} className="border-t">
                        <td className="px-3 py-2">
                          {isEditing ? (
                            <input
                              type="number"
                              step="0.01"
                              value={editRule.price}
                              onChange={(event) => setEditRule((prev) => ({ ...prev, price: event.target.value }))}
                              className="w-32 rounded border border-slate-300 px-2 py-1 text-sm"
                              disabled={!canEdit}
                            />
                          ) : (
                            rule.price
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {isEditing ? (
                            <input
                              type="date"
                              value={editRule.effective_from}
                              onChange={(event) =>
                                setEditRule((prev) => ({ ...prev, effective_from: event.target.value }))
                              }
                              className="rounded border border-slate-300 px-2 py-1 text-sm"
                              disabled={!canEdit}
                            />
                          ) : (
                            normalizeDateValue(rule.effective_from)
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {isEditing ? (
                            <input
                              type="date"
                              value={editRule.effective_to || ""}
                              onChange={(event) =>
                                setEditRule((prev) => ({ ...prev, effective_to: event.target.value }))
                              }
                              className="rounded border border-slate-300 px-2 py-1 text-sm"
                              disabled={!canEdit}
                            />
                          ) : (
                            normalizeDateValue(rule.effective_to) || "No expiration"
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {isEditing ? (
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                className="rounded bg-blue-600 px-3 py-1 text-xs font-semibold text-white"
                                onClick={handleUpdateRule}
                                disabled={!canEdit}
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                className="rounded border border-slate-300 px-3 py-1 text-xs"
                                onClick={cancelEditRule}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                className="text-xs text-blue-600 hover:underline disabled:opacity-60"
                                onClick={() => startEditRule(rule)}
                                disabled={!canEdit}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="text-xs text-red-600 hover:underline disabled:opacity-60"
                                onClick={() => handleDeleteRule(rule.id)}
                                disabled={!canEdit}
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="border-t pt-4">
              <h3 className="text-sm font-semibold text-slate-700">Add pricing rule</h3>
              <div className="mt-3 grid gap-3 md:grid-cols-4">
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Price
                  <input
                    type="number"
                    step="0.01"
                    value={newRule.price}
                    onChange={(event) => setNewRule((prev) => ({ ...prev, price: event.target.value }))}
                    className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm"
                    disabled={!canEdit}
                  />
                </label>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Effective From
                  <input
                    type="date"
                    value={newRule.effective_from}
                    onChange={(event) => setNewRule((prev) => ({ ...prev, effective_from: event.target.value }))}
                    className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm"
                    disabled={!canEdit}
                  />
                </label>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Effective To
                  <input
                    type="date"
                    value={newRule.effective_to}
                    onChange={(event) => setNewRule((prev) => ({ ...prev, effective_to: event.target.value }))}
                    className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm"
                    disabled={!canEdit}
                  />
                </label>
                <div className="flex items-end">
                  <button
                    type="button"
                    className="w-full rounded bg-emerald-600 px-3 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                    onClick={handleCreateRule}
                    disabled={!canEdit}
                  >
                    Add Rule
                  </button>
                </div>
              </div>
              <p className="mt-2 text-xs text-slate-500">
                Effective To can be left blank to indicate no expiration.
              </p>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
